/**
 * Dashboard tenant context: selected tenant for platform admins on the home dashboard.
 *
 * Pure derivative of the global TenantContext:
 * - Platform admins: reflects currentTenant.id, or 'all' when the "All Tenants"
 *   option is active (currentTenant === null, set via the header switcher on the home page).
 * - Regular users: reflects currentTenant.id (backend always scopes to their tenant).
 *
 * The "All Tenants" option is only available in the app-header tenant switcher when the
 * user is on the home page ('/').  Navigating away auto-reverts to the home tenant
 * (handled in AppLayout).
 */

import React, { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTenant } from '@contexts/TenantContext'
import { platformAdminApi } from '@services/api'
import type { Tenant } from '@types/tenant'

export interface DashboardTenantContextValue {
  /** Selected tenant ID for dashboard metrics. 'all' means aggregate view (platform admin only). */
  dashboardTenantId: string | null
  /** No-op: tenant selection is driven by the global header switcher. */
  setDashboardTenantId: (id: string | null) => void
}

const defaultValue: DashboardTenantContextValue = {
  dashboardTenantId: null,
  setDashboardTenantId: () => {},
}

const DashboardTenantContext = createContext<DashboardTenantContextValue>(defaultValue)

export function DashboardTenantProvider({ children }: { children: ReactNode }) {
  const { currentTenant, isPlatformAdmin } = useTenant()

  const dashboardTenantId = useMemo(() => {
    if (isPlatformAdmin) return currentTenant?.id ?? 'all'
    return currentTenant?.id ?? null
  }, [currentTenant, isPlatformAdmin])

  const value = useMemo<DashboardTenantContextValue>(
    () => ({ dashboardTenantId, setDashboardTenantId: () => {} }),
    [dashboardTenantId]
  )

  return (
    <DashboardTenantContext.Provider value={value}>
      {children}
    </DashboardTenantContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- hook must live with the provider
export function useDashboardTenant(): DashboardTenantContextValue {
  return useContext(DashboardTenantContext)
}

/**
 * Lazy platform-admin tenant list for the header switcher.
 * Query stays disabled until `enabled` is true (dropdown opened).
 */
// eslint-disable-next-line react-refresh/only-export-components -- lazy query hook used by TenantSwitcher
export function useLazyPlatformTenantList(
  enabled: boolean,
  search: string = '',
): { tenants: Tenant[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['platform-admin', 'tenant-list', search],
    queryFn: () =>
      platformAdminApi.listTenants({
        page_size: 50,
        search: search || undefined,
        status: 'active',
      }),
    enabled,
    staleTime: 60_000,
  })
  return { tenants: data?.items ?? [], isLoading }
}
